import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0302 from "../lesson-02/lesson";
import lesson0317 from "../lesson-17/lesson";

const linkLesson0306 = getLinkForCourseLesson(
  "49817b85-0b30-4661-8a25-1e5176041865",
  "8ab760c5-1680-4f6f-b194-ae3cae6b1c5e"
);
const linkLesson0302 = getLinkForCourseLesson("49817b85-0b30-4661-8a25-1e5176041865", lesson0302.id);
const linkLesson0317 = getLinkForCourseLesson("49817b85-0b30-4661-8a25-1e5176041865", lesson0317.id);

const theory = `<h3 class=sectionHead>Introduction</h3>
<p>
   To auralize sound sources in a virtual room, it is required to calculate the room impulse response between source and receiver [2]. For the frequency range above the Schroeder-frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>S</mi>
         </mrow>
      </msub>
   </math>
    [3], geometrical methods can be applied as discussed in <a href="${linkLesson0306}" target="_blank">Lesson 3.6</a>. A simple geometrical approach is the image source method [1], which will be introduced in this lesson. In literature the image source model is also referred to as the <em><span class=cmti-10>image </span><span class=cmti-10>method</span></em>, <em><span class=cmti-10>mirror image source method</span></em> or the <em><span class=cmti-10>mirror source method</span></em>.
<h3 class=sectionHead>Image source model</h3>
<p>
   The image source model obtains audible sound reflections, emitted by a point sound source in a room, for a specified receiving point. It is based on the determination of all reflections by creating corresponding virtual image sources. The image sources are generated by mirroring sources on the room’s walls. In the first step, the original sound source
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   is mirrored on all walls
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>i</mi>
      <mo class=MathClass-rel>=</mo>
      <mo class=MathClass-open>{</mo>
      <mn>1</mn>
      <mo class=MathClass-punc>,</mo>
      <mn>2</mn>
      <mo class=MathClass-punc>,</mo>
      <mo class=MathClass-punc>.</mo>
      <mo class=MathClass-punc>.</mo>
      <mo class=MathClass-punc>.</mo>
      <mo class=MathClass-punc>,</mo>
      <mi>M</mi>
      <mo class=MathClass-close>}</mo>
   </math>
   of the room (see <a href="#" data-img-src="${config.ASSETS_URL}/03/08/theory/figure-1.svg" data-img-alt="Figure 1. Construction of first order image sources for a simple two-dimensional room. Two of the eight first order image sources are invalid.">Figure 1</a>).
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/03/08/theory/figure-1.svg" alt="Figure 1. Construction of first order image sources for a simple two-dimensional room. Two of the eight first order image sources are invalid."><figcaption class=caption>Figure 1. Construction of first order image sources for a simple two-dimensional room. Two of the eight first order image sources are invalid.</figcaption></figure>
<p>
   Each of the generated first order image sources
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   is then mirrored again on all walls except the wall on which it was mirrored in the previous step, resulting in the image sources of the second order
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>i</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <msub>
               <mrow>
                  <mi>i</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msub>
         </mrow>
      </msub>
   </math>
   . This process is repeated until a predefined maximum order
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>N</mi>
   </math>
   is reached.<br class=newline>The walls of a room are typically referred to as faces or surfaces, which are also defined by a normal vector
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>n</mi>
         </mrow>
         <mo accent=true>→</mo>
      </mover>
   </math>
   pointing towards the inside of a room. As some series of reflections are impossible, some image sources and their children can be skipped during calculations:
<ul class=itemize1>
   <li class=itemize>
      No reflection occurs twice in a row on the same surface, e.g.,
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mn>3</mn>
                  <mn>5</mn>
                  <mn>5</mn>
            </mrow>
         </msub>
      </math>
      and its children can be dismissed.
   <li class=itemize>
      Sound rays can only be reflected at surfaces that point towards the sound/image source. If the sound source is behind a wall, no image source has to be generated for this surface (cf. <a href="#" data-img-src="${config.ASSETS_URL}/03/08/theory/figure-2.svg" data-img-alt="Figure 2. Creation of (in-)valid image sources.">Figure 2</a>). Two examples for such kind of invalid image sources are
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mn>6</mn>
            </mrow>
         </msub>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mn>4</mn>
            </mrow>
         </msub>
      </math>
      in <a href="#" data-img-src="${config.ASSETS_URL}/03/08/theory/figure-1.svg" data-img-alt="Figure 1. Construction of first order image sources for a simple two-dimensional room. Two of the eight first order image sources are invalid.">Figure 1</a>.
</ul>
<figure class=figure>
   <p>
      <img src="${config.ASSETS_URL}/03/08/theory/figure-2.svg" alt="Figure 2. Creation of (in-)valid image sources.">
      <figcaption class=caption>Figure 2. Creation of (in-)valid image sources.</figcaption>
</figure>
<p>Once all image sources are created, the sound path has to be traced back from the receiver to the main sound source in order to check the audibility of the image source. This verification process is in fact a visibility check, but in the context of acoustical situation often called <em><strong><span class=cmbxti-10>audibility test</span></strong></em>. Note that the majority of image sources are typically not audible for the receiver. The test traces the reflection path of each image source, starting at the receiver.<br class=newline>
<table class=equation>
   <tr>
      <td style="font-size: 8px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <mi>R</mi>
            <mo class=MathClass-rel>→</mo>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                        <msub>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
                                                <msub>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                     <mrow>
                        <mi>N</mi>
                     </mrow>
                  </msub>
               </mrow>
            </msub>
            <mo class=MathClass-rel>→</mo>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                        <msub>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
                                                <msub>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                     <mrow>
                        <mi>N</mi>
                        <mo class=MathClass-bin>−</mo>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
            </msub>
            <mo class=MathClass-rel>→</mo>
            <mo class=MathClass-rel>→</mo>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
                        <msub>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msub>
               </mrow>
            </msub>
            <mo class=MathClass-rel>→</mo>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>i</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                  </msub>
               </mrow>
            </msub>
            <mo class=MathClass-rel>→</mo>
            <mi>S</mi>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   It checks if the sound path hits the correct walls in correct order. A wall hit is valid, if an intersection point is found for the corresponding wall polygon and the sound path did not intersect with any other wall before. This step-wise process is visualized in <a href="#" data-img-src="${config.ASSETS_URL}/03/08/theory/figure-3.svg" data-img-alt="Figure 3. Image source audibility test for image source <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>S</mi></mrow><mrow><mn>1</mn><mo class=MathClass-punc>,</mo><mn>3</mn></mrow></msub></math> - reflection path from receiver to source ( a <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mo class=MathClass-rel>→</mo></math> b <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mo class=MathClass-rel>→</mo></math> c ) is valid.">Figure 3</a> for an audible second order image source. At the last reflection point before the sound source
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>S</mi>
   </math>
   is reached, it is also required to check the visibility of the sound source from this reflection point (cf. path c in <a href="#" data-img-src="${config.ASSETS_URL}/03/08/theory/figure-3.svg" data-img-alt="Figure 3. Image source audibility test for image source <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>S</mi></mrow><mrow><mn>1</mn><mo class=MathClass-punc>,</mo><mn>3</mn></mrow></msub></math> - reflection path from receiver to source ( a <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mo class=MathClass-rel>→</mo></math> b <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mo class=MathClass-rel>→</mo></math> c ) is valid.">Figure 3</a>). If for any step, the visibility test fails, the test for the corresponding image source can be stopped and this image source is considered to be non-audible for the current receiver position. If the receiver moves, the audibility tests for all image sources have to be repeated.
   <figure class=figure>
<p><img src="${config.ASSETS_URL}/03/08/theory/figure-3.svg" alt="Figure 3. Image source audibility test for image source <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>S</mi></mrow><mrow><mn>1</mn><mo class=MathClass-punc>,</mo><mn>3</mn></mrow></msub></math> - reflection path from receiver to source ( a <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mo class=MathClass-rel>→</mo></math> b <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mo class=MathClass-rel>→</mo></math> c ) is valid."><figcaption class=caption>Figure 3. Image source audibility test for image source <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>S</mi></mrow><mrow><mn>1</mn><mo class=MathClass-punc>,</mo><mn>3</mn></mrow></msub></math> - reflection path from receiver to source ( a <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mo class=MathClass-rel>→</mo></math> b <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mo class=MathClass-rel>→</mo></math> c ) is valid.</figcaption></figure>
<p>
   A visualization of audible reflection paths based on the image source model in a 3D model is presented in the following video.
   <figure class=figure>
<p>[ <strong><a href=https://rwth-aachen.sciebo.de/s/rp5SpDwpgHJoR9G target="_blank"><span class=cmbx-10>Click to see Video (55 seconds)</span></a></strong> ]</figure>
<h3 class=sectionHead>Room impulse response synthesis</h3>
<p>
   As introduced in <a href="${linkLesson0302}" target="_blank">Lesson 3.2</a>, a room impulse response can be used to describe the properties of a room. Based on the calculated audible image sources, a room impulse response can be synthesized. For authentic room auralizations, the room impulse response has to include information about the sound source directivity, the room and its reflections and the receiver directivity [4]. Details on this are presented in <a href="${linkLesson0317}" target="_blank">Lesson 3.17</a>, in this lesson the contribution of each impulse response is introduced in a simplified way, assuming a frequency independent reflection factor
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mn>1</mn>
            <mo class=MathClass-bin>−</mo>
            <msub>
               <mrow>
                  <mi>α</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
         </mrow>
      </msqrt>
   </math>
   for wall
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>i</mi>
   </math>
   and also neglecting the air absorption in the room.<br class=newline>Audible image source reflections can be integrated into the impulse response by using dirac impulses, delayed by the corresponding traveling time (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi mathvariant=italic>Δt</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>r</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>c</mi>
   </math>
   ). The dirac impulse has to be attenuated by the distance law (1/r) to account for spherical wave propagation loss. Equation 2 describes the contribution of an image source of order
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>N</mi>
   </math>
   with the reflection path
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>j</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>i</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-punc>,</mo>
      <mo class=MathClass-punc>.</mo>
      <mo class=MathClass-punc>.</mo>
      <mo class=MathClass-punc>.</mo>
      <mo class=MathClass-punc>,</mo>
      <msub>
         <mrow>
            <mi>i</mi>
         </mrow>
         <mrow>
            <mi>N</mi>
         </mrow>
      </msub>
   </math>
   in the time-domain:<br class=newline>
<table class=equation>
   <tr>
      <td>
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>h</mi>
               </mrow>
               <mrow>
                  <mi>j</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mi>j</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <munder class=msub>
               <mrow>
                  <mo>∏</mo>
               </mrow>
               <mrow>
                  <mi>j</mi>
               </mrow>
            </munder>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>i</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>∗</mo>
            <mi>δ</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mi>j</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi>j</mi>
         </mrow>
      </msub>
   </math>
   describing the length of the reflection path, directly corresponding to the distance between receiver
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>R</mi>
   </math>
   and the image source
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mi>j</mi>
         </mrow>
      </msub>
   </math>
   . The product sum including the wall reflection factors
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>i</mi>
         </mrow>
      </msub>
   </math>
   describes the attenuation due to wall absorption. For the direct sound, this term equals to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
   </math>
   . Furthermore, it is assumed that the sound source at a distance of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
   </math>
   m has an amplitude of 1.<br class=newline>
<p>
   The sum over all audible image sources
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>J</mi>
   </math>
   and the direct sound (if audible), leads to the total room impulse response
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>h</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>total</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   :
<table class=equation>
   <tr>
      <td style="font-size: 11px;">
         <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
            <msub>
               <mrow>
                  <mi>h</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>total</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>src</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-bin>⋅</mo>
            <mi>δ</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-bin>−</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>r</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>src</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <munder class=msub>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>J</mi>
               </mrow>
            </munder>
            <msub>
               <mrow>
                  <mi>h</mi>
               </mrow>
               <mrow>
                  <mi>j</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open stretchy=false>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close stretchy=false>)</mo>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>r</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>src</mi>
         </mrow>
      </msub>
   </math>
   being the distance from receiver to the sound source in the room.<br class=newline>
<p>
   Because of an exponential growth of the number of image sources for increasing orders, the image source model is often only applied up to orders
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>N</mi>
      <mo class=MathClass-rel>≤</mo>
      <mn>3</mn>
   </math>
   . If the total numbers of image sources is kept at a low level, it is an efficient model to generate temporally accurate early reflections of a room impulse response and can be used in combination with other simulation models. Another downside, in addition to the inefficiency for higher orders, is the missing inclusion of diffuse reflections.
<h3 class=sectionHead>References</h3>
   <dl class=thebibliography>
     <dt>[1]
     <dd>
      J. B. Allen and D. A. Berkley. Image method for efficiently simulating small-room acoustics. J. Acoust. Soc. Am., 65(4):943–950, Apr. 1979.
     <dt>[2]
     <dd>
      H. Kuttruff. Room acoustics. Spon Press, Milton Park, Abingdon, United Kingdom, 5th edition, 2009.
     <dt>[3]
     <dd>
      M. R. Schroeder. New method of measuring reverberation time. J. Acoust. Soc. Am., 37:409–412, 1965.
    <dt>[4]
    <dd>
      M. Vorl ̈ander. Auralization. Fundamentals of acoustics, modelling, simulation, algorithms and acoustic virtual reality. Springer, Berlin, Heidelberg, Germany, 1st edition, 2008.
   </dl>
`;

export default theory;
